import {Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {
    ContractControlListingStatusFilter
} from 'app/main/pages/contract-control/contract-control-filters/contract-control-filters.component';
import {ToastrService} from 'ngx-toastr';
import {combineLatest, firstValueFrom} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {IGetCategoriesItemDto} from '../../pages/categories.dto';
import {CategoryService} from '../../pages/category.service';
import {DefaultFilter} from '../../pages/contract-control/contract-control-interfaces/default-filters.interface';

import {IDocumentTypesDto} from '../../pages/documents/dto/document-listing.dto';
import {ISearchableRequest} from '../../pages/users/userslisting/dto/get-user-list.dto';
import {SharedStoreFacade} from '../shared/store/shared-store.facade';
import {AdvancedFilterFieldTypesEnum, IAdvanceFilter} from './components/advance-filter';


@Injectable({
    providedIn:'root'
})
export class AdvancedFilterHelper{
    constructor(
        protected _toastr:ToastrService, 
        protected _translocoService:TranslocoService,
        protected _sharedStoreFacade$: SharedStoreFacade,
        protected _categoryService: CategoryService) {
    }


    validateRestoredAdvancedFilters<T extends IAdvanceFilter<unknown>>(advancedFilters: T[]) {
        const containsUndefined = advancedFilters?.some(x=>x === null);
        if(containsUndefined){
            this._toastr.warning(this._translocoService.translate('Advanced filters not fully restored warning toast (restore advanced filter) - Not all advanced filters has been restored'));
            return this.removeUndefinedAdvancedFilters<T>(advancedFilters);
        }

        return advancedFilters;
    }



    private removeUndefinedAdvancedFilters<T extends IAdvanceFilter<unknown>>(advancedFilters: T[]) {
        for (let i = 0; i < advancedFilters.length; i++) {
            //   if (advancedFilters[i] === null && typeCheckFn(arr[i + 1])) {
            if (advancedFilters[i] === null) {
                advancedFilters.splice(i, 1); // Remove current and next element

                if(advancedFilters.length > i && i >= 0) {
                    if (advancedFilters[i].field_type === AdvancedFilterFieldTypesEnum.condition) {
                        advancedFilters.splice(i, 1); // Remove current and next element
                    }
                }
                i--; // Adjust index to account for element removal
            }
        }
        return advancedFilters;
    }

}

@Injectable({
    providedIn:'root'
})
export class CCAdvancedFilterHelper extends AdvancedFilterHelper{

    retrieveBasicFilters(value): Promise<DefaultFilter> {
        return firstValueFrom(
            combineLatest({
                documentTypes: this._sharedStoreFacade$.documentTypes$.pipe(take(1)),
                categories: this._categoryService.fetchCategories('all').pipe(take(1))
            })
                .pipe(map(({documentTypes, categories})=>{
                    return new ContractControlRestoreBasicFilterBuilder(this._toastr, this._translocoService, documentTypes, categories as IGetCategoriesItemDto[])
                        .restoreSearch(value?.search)
                        .restoreStatus(value?.status)
                        .restoreDocumentType(value?.document_type_id)
                        .restoreCategoryType(value?.category_ids || value?.category_id)
                        .create()
                }))
        )

    }
}

export class ContractControlRestoreBasicFilterBuilder {
    private filters = {} as DefaultFilter;
    private readonly noCategoryValue = 'no_category';
    private readonly noCategoryId = -1;
    private containsCategoryHelper: ContainsCategoryHelper = new ContainsCategoryHelper();
    private documentTypeHelper: DocumentTypeHelper = new DocumentTypeHelper();

    constructor(private _toastr:ToastrService ,private _translocoService:TranslocoService , private documentTypes: IDocumentTypesDto[], private categories: IGetCategoriesItemDto[]) {
    }

    restoreSearch(search: string){
        if(search && search.length > 0){
            (<ISearchableRequest>this.filters).search = search;
        }

        return this;
    }

    restoreStatus(status:string){
        if (status) {
            const isIncluded = ContractControlListingStatusFilter.some(x=>x.value?.toLocaleLowerCase().trim() == status.toLocaleLowerCase().trim());
            if(!isIncluded){
                this._toastr.warning(this._translocoService.translate('Status not restored notification by saved filters (CC and Documents default filters) - Saved status filter was not be restored'));
            }
            
            this.filters.status = isIncluded ? status : ContractControlListingStatusFilter[0].value;
        }
        else {
            this.filters.status = null;
        }

        return this;
    }


    restoreDocumentType(document_type_id:number){
        if (document_type_id) {
            const isIncluded = this.documentTypeHelper.includeDocumentType(document_type_id, this.documentTypes);
            if(!isIncluded){
                this._toastr.warning(this._translocoService.translate('Document type not restored notification by saved filters (CC and Documents default filters) - Saved document type filter was not be restored'));
            }

            this.filters.document_type = isIncluded ? document_type_id : null;
        }else{
            this.filters.document_type = null
        }
        return this;
    }

    restoreCategoryType(category_ids: number | number[] | string) {
        if (category_ids === this.noCategoryValue) {
            this.filters.category_ids = this.noCategoryId;
            return this;
        }

        if (category_ids) {
            const normalizedCategoryIds = Array.isArray(category_ids)
                ? category_ids.map(id => Number(id)).filter(id => !Number.isNaN(id))
                : [Number(category_ids)].filter(id => !Number.isNaN(id));

            const isIncluded = normalizedCategoryIds.length > 0
                && normalizedCategoryIds.every(id => this.containsCategoryHelper.includeCategory(id, this.categories));

            if (!isIncluded) {
                this._toastr.warning(this._translocoService.translate('Category not restored notification by saved filters (CC and Documents default filters) - Saved category filter was not be restored'));
            }

            this.filters.category_ids = isIncluded
                ? (Array.isArray(category_ids) ? normalizedCategoryIds : normalizedCategoryIds[0])
                : null;
        } else {
            this.filters.category_ids = null;
        }

        return this;
    }

    create(){
        return this.filters
    }
}

export class ContainsCategoryHelper {

    public includeCategory (category_id:number, categories: IGetCategoriesItemDto[]){
        return categories?.some(x=> this.containsCategory(category_id, x))
    }

    private containsCategory(categoryId:number, subCategory:IGetCategoriesItemDto){
        if(subCategory.id === categoryId){
            return true
        }else{
            return subCategory?.children?.some(x=> this.containsCategory(categoryId, x) );
        }
    }
}

export class DocumentTypeHelper {

    public includeDocumentType (document_type_id:number, documentTypes: IDocumentTypesDto[]){
        return documentTypes?.some(x=>x.id === Number(document_type_id));
    }

}
